import React, { useState, useMemo } from "react";
import Chart from "react-apexcharts";

const CHART_TYPE_OPTIONS = [
  { value: "area", label: "Area Chart" },
  { value: "bar", label: "Bar Chart" },
  { value: "line", label: "Line Chart" },
];

const VIEW_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "hotel", label: "By Hotel" },
];

const YEAR_OPTIONS = ["2025", "2026"];

export const CancellationTrendSection = ({
  cancellationData,
  cancellationLoading,
  cancellationError,
}) => {
  const [chartType, setChartType] = useState("area");
  const [viewMode, setViewMode] = useState("daily");
  const [selectedYear, setSelectedYear] = useState("2026");

  const { dailySeries, dailyOptions, hotelSeries, hotelOptions, totalCancelled } = useMemo(() => {
    const filteredCancellationData = (cancellationData || []).filter((item) => {
      if (!item?.CancelDate) return false;
      return new Date(item.CancelDate).getFullYear().toString() === selectedYear;
    });

    if (!filteredCancellationData.length) {
      return {
        dailySeries: [],
        dailyOptions: {},
        hotelSeries: [],
        hotelOptions: {},
        totalCancelled: 0,
      };
    }

    // --- Daily aggregation ---
    const dailyMap = new Map();
    let total = 0;

    filteredCancellationData.forEach((item) => {
      const dateStr = item.CancelDate
        ? new Date(item.CancelDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : null;
      if (!dateStr) return;

      const rooms = Number(item.CancelledRooms) || 0;
      total += rooms;
      dailyMap.set(dateStr, (dailyMap.get(dateStr) || 0) + rooms);
    });

    const sortedDailyEntries = [...dailyMap.entries()].sort(
      (a, b) => new Date(a[0]) - new Date(b[0]),
    );
    const dailyCategories = sortedDailyEntries.map(([date]) => date);
    const dailyValues = sortedDailyEntries.map(([, count]) => count);

    // --- Hotel aggregation ---
    const hotelMap = new Map();
    filteredCancellationData.forEach((item) => {
      const hotelName = item.vchUnitName || `Hotel ${item.intPropertyId}`;
      const rooms = Number(item.CancelledRooms) || 0;
      hotelMap.set(hotelName, (hotelMap.get(hotelName) || 0) + rooms);
    });

    const sortedHotelEntries = [...hotelMap.entries()].sort((a, b) => b[1] - a[1]);
    const hotelCategories = sortedHotelEntries.map(([name]) => name);
    const hotelValues = sortedHotelEntries.map(([, count]) => count);

    const sharedTooltip = {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex][dataPointIndex];
        const category = w.globals.labels[dataPointIndex] || "";
        return `
          <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); padding: 16px; min-width: 180px;">
            <div style="font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 8px; font-family: Inter, sans-serif;">Cancelled Rooms</div>
            <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px;">
              <span style="font-size: 26px; font-weight: 700; color: #ef4444; line-height: 1; font-family: Inter, sans-serif;">${value}</span>
              <span style="background: #fef2f2; color: #dc2626; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; border: 1px solid #fecaca; display: flex; align-items: center; height: 24px;">Rooms</span>
            </div>
            <div style="font-size: 13px; color: #6b7280; font-family: Inter, sans-serif; background: #f9fafb; margin: 0 -16px -16px -16px; padding: 12px 16px; border-top: 1px solid #e5e7eb; border-bottom-left-radius: 6px; border-bottom-right-radius: 6px;">
              ${category}
            </div>
          </div>
        `;
      },
    };

    const dOpts = {
      chart: {
        type: "area",
        toolbar: { show: false },
        zoom: { enabled: true },
      },
      colors: ["#ef4444"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      stroke: {
        curve: "smooth",
        width: 2.5,
      },
      grid: {
        borderColor: "#f3f4f6",
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      xaxis: {
        categories: dailyCategories,
        title: { text: "Date" },
        labels: {
          rotate: -45,
          rotateAlways: dailyCategories.length > 10,
          style: { fontSize: "11px" },
        },
        crosshairs: {
          show: true,
          position: "back",
          stroke: { color: "#111827", width: 1, dashArray: 0 },
        },
        tooltip: { enabled: false },
      },
      yaxis: {
        title: { text: "Cancelled Rooms" },
        labels: {
          formatter: (value) => Math.round(value),
        },
        min: 0,
      },
      tooltip: sharedTooltip,
      dataLabels: { enabled: false },
      markers: { size: 3, hover: { size: 6 } },
    };

    const dSeries = [
      {
        name: "Cancelled Rooms",
        data: dailyValues,
      },
    ];

    const hOpts = {
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      colors: ["#f97316"],
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: true,
          distributed: true,
          barHeight: "60%",
        },
      },
      grid: {
        borderColor: "#f3f4f6",
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      xaxis: {
        title: { text: "Total Cancelled Rooms" },
        labels: {
          formatter: (value) => Math.round(value),
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: "12px" },
          maxWidth: 220,
        },
      },
      tooltip: {
        y: {
          formatter: (val) => `${val} Rooms`,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "12px",
          fontWeight: 600,
        },
      },
      legend: { show: false },
    };

    const hSeries = [
      {
        name: "Cancelled Rooms",
        data: sortedHotelEntries.map(([name, count]) => ({
          x: name,
          y: count,
        })),
      },
    ];

    return {
      dailySeries: dSeries,
      dailyOptions: dOpts,
      hotelSeries: hSeries,
      hotelOptions: hOpts,
      totalCancelled: total,
    };
  }, [cancellationData, selectedYear]);

  const resolvedDailyOptions = useMemo(() => {
    if (!dailyOptions.chart) return dailyOptions;

    const base = { ...dailyOptions };

    if (chartType === "bar") {
      return {
        ...base,
        chart: { ...base.chart, type: "bar" },
        plotOptions: {
          bar: { borderRadius: 4, columnWidth: "55%" },
        },
        fill: { type: "solid", opacity: 0.85 },
        stroke: { width: 0 },
        markers: { size: 0 },
      };
    }

    if (chartType === "line") {
      return {
        ...base,
        chart: { ...base.chart, type: "line" },
        fill: { type: "solid", opacity: 0 },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4, hover: { size: 7 } },
      };
    }

    return base; // area (default)
  }, [dailyOptions, chartType]);

  const chartHeight = useMemo(() => {
    if (viewMode === "hotel" && hotelSeries.length) {
      const barCount = hotelSeries[0]?.data?.length || 0;
      return Math.max(300, barCount * 38 + 80);
    }
    return 350;
  }, [viewMode, hotelSeries]);

  return (
    <div className="row g-3 mb-3 mt-1">
      <div className="col-12">
        <div className="card" style={{ border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div
            className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2"
            style={{
              background: "linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)",
              borderBottom: "1px solid #fecaca",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 18,
                  flexShrink: 0,
                }}
              >
                ✕
              </div>
              <div>
                <h6 className="card-title mb-0" style={{ fontSize: 15, fontWeight: 700 }}>
                  Cancellation Trend
                </h6>
                {!cancellationLoading && totalCancelled > 0 && (
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    Total: <strong style={{ color: "#ef4444" }}>{totalCancelled}</strong> rooms
                    cancelled
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-end">
              <div>
                <label className="form-label mb-1" style={{ fontSize: 12 }}>
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ minWidth: 100 }}
                >
                  {YEAR_OPTIONS.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label mb-1" style={{ fontSize: 12 }}>
                  View
                </label>
                <select
                  className="form-select form-select-sm"
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  style={{ minWidth: 110 }}
                >
                  {VIEW_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {viewMode === "daily" && (
                <div>
                  <label className="form-label mb-1" style={{ fontSize: 12 }}>
                    Chart Type
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    style={{ minWidth: 120 }}
                  >
                    {CHART_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="card-body">
            {cancellationLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <div className="spinner-border text-danger" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : cancellationError ? (
              <div className="alert alert-warning text-center mb-0">{cancellationError}</div>
            ) : viewMode === "daily" ? (
              dailySeries.length > 0 ? (
                <Chart
                  key={`cancel-daily-${chartType}`}
                  options={resolvedDailyOptions}
                  series={dailySeries}
                  type={chartType}
                  height={350}
                />
              ) : (
                <div className="text-center text-muted py-5">
                  No cancellation data available for {selectedYear}.
                </div>
              )
            ) : hotelSeries.length > 0 && hotelSeries[0]?.data?.length > 0 ? (
              <Chart
                key="cancel-hotel"
                options={hotelOptions}
                series={hotelSeries}
                type="bar"
                height={chartHeight}
              />
            ) : (
              <div className="text-center text-muted py-5">
                No hotel-wise cancellation data available for {selectedYear}.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
